import { eventsForLocalDay, layoutDayEvents } from './calendarEventLayout';
import { gymCalendarCard, gymCalendarGridHeader, gymDarkBorder, gymDarkMutedText } from './gymPageTheme';

const DAY_START_HOUR = 8;
const DAY_END_HOUR = 22;
const HOUR_HEIGHT = 56;
const HOURS = Array.from({ length: DAY_END_HOUR - DAY_START_HOUR }, (_, i) => i + DAY_START_HOUR);

function eventColorClass(event) {
    if (event.is_course && event.color) return '';
    switch (event.event_type) {
        case 'training':
            return 'bg-blue-500 hover:bg-blue-600';
        case 'consultation':
            return 'bg-green-500 hover:bg-green-600';
        case 'evaluation':
            return 'bg-purple-500 hover:bg-purple-600';
        case 'other':
            return 'bg-orange-500 hover:bg-orange-600';
        default:
            return 'bg-indigo-500 hover:bg-indigo-600';
    }
}

function formatHour(dateTime) {
    try {
        return new Date(dateTime).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    } catch (_) {
        return '';
    }
}

function isSameDay(a, b) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function EventBlock({ event, top, height, left, width, onOpen, onHover, onLeave }) {
    const style = {
        top,
        height,
        left,
        width,
        backgroundColor: event.is_course && event.color ? event.color : undefined,
    };
    return (
        <button
            type="button"
            className={`absolute overflow-hidden rounded-md px-1.5 py-1 text-left text-white ${eventColorClass(event)} ${event.is_course ? 'ring-1 ring-white/40' : ''}`}
            style={style}
            onMouseEnter={(e) => onHover(event, e)}
            onMouseLeave={onLeave}
            onClick={(e) => {
                e.stopPropagation();
                onOpen(event);
            }}
        >
            <div className="truncate text-[13px] font-semibold leading-tight">{event.title?.trim() || 'Evento'}</div>
            <div className="truncate text-[11px] opacity-90">
                {formatHour(event.start_datetime)}–{formatHour(event.end_datetime)}
            </div>
        </button>
    );
}

function DayColumn({ date, events, canCreate, onEmptySlot, onOpen, onHover, onLeave }) {
    const laidOut = layoutDayEvents(eventsForLocalDay(events, date));
    const columnHeight = HOURS.length * HOUR_HEIGHT;
    return (
        <div
            className={`relative border-l border-gray-200 ${gymDarkBorder} ${canCreate ? 'cursor-pointer' : ''}`}
            style={{ height: columnHeight }}
            onClick={(e) => {
                if (!canCreate) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const hour = DAY_START_HOUR + Math.floor((e.clientY - rect.top) / HOUR_HEIGHT);
                const clamped = Math.min(Math.max(hour, DAY_START_HOUR), DAY_END_HOUR - 1);
                onEmptySlot(date, clamped);
            }}
        >
            {HOURS.map((hour) => (
                <div
                    key={hour}
                    className={`border-b border-gray-100 ${gymDarkBorder}`}
                    style={{ height: HOUR_HEIGHT }}
                />
            ))}
            {laidOut.map(({ event, col, cols, startMs, endMs }) => {
                const start = new Date(startMs);
                const minutesFromStart = (start.getHours() * 60 + start.getMinutes()) - DAY_START_HOUR * 60;
                const durationMin = Math.max((endMs - startMs) / 60000, 20);
                const top = (minutesFromStart / 60) * HOUR_HEIGHT;
                const height = Math.max((durationMin / 60) * HOUR_HEIGHT - 2, 22);
                const widthPct = 100 / cols;
                return (
                    <EventBlock
                        key={event.id}
                        event={event}
                        top={top}
                        height={height}
                        left={`calc(${col * widthPct}% + 2px)`}
                        width={`calc(${widthPct}% - 4px)`}
                        onOpen={onOpen}
                        onHover={onHover}
                        onLeave={onLeave}
                    />
                );
            })}
        </div>
    );
}

export default function CalendarBoard({
    view,
    selectedDate,
    weekDays,
    events,
    canCreate,
    dayName,
    onEmptySlot,
    onOpen,
    onHover,
    onLeave,
    onPickDay,
    moreLabel,
}) {
    const today = new Date();

    if (view === 'month') {
        const year = selectedDate.getFullYear();
        const month = selectedDate.getMonth();
        const first = new Date(year, month, 1);
        const startPad = (first.getDay() + 6) % 7;
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const cells = [];
        for (let i = 0; i < startPad; i += 1) cells.push(null);
        for (let day = 1; day <= daysInMonth; day += 1) cells.push(new Date(year, month, day));
        while (cells.length % 7 !== 0) cells.push(null);
        const weekdayLabels = [0, 1, 2, 3, 4, 5, 6].map((offset) => dayName(new Date(2026, 8, 21 + offset)));

        return (
            <div className={`${gymCalendarCard} overflow-hidden`}>
                <div className="grid grid-cols-7">
                    {weekdayLabels.map((label) => (
                        <div key={label} className={`p-2 text-center text-xs font-medium ${gymCalendarGridHeader}`}>
                            {label}
                        </div>
                    ))}
                    {cells.map((date, index) => {
                        const dayEvents = date ? eventsForLocalDay(events, date) : [];
                        const shown = dayEvents.slice(0, 3);
                        return (
                            <div
                                key={date ? date.toISOString() : `empty-${index}`}
                                className={`min-h-[108px] border-t border-l border-gray-200 p-1 ${gymDarkBorder} ${date && isSameDay(date, today) ? 'bg-indigo-500/10' : ''}`}
                            >
                                {date && (
                                    <button
                                        type="button"
                                        className="mb-1 flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold text-gray-800 hover:bg-white/10 dark:text-white"
                                        onClick={() => onPickDay(date)}
                                    >
                                        {date.getDate()}
                                    </button>
                                )}
                                <div className="space-y-0.5">
                                    {shown.map((event) => (
                                        <button
                                            key={event.id}
                                            type="button"
                                            className={`block w-full truncate rounded px-1 text-left text-[11px] text-white ${eventColorClass(event)}`}
                                            style={event.is_course && event.color ? { backgroundColor: event.color } : undefined}
                                            onClick={() => onOpen(event)}
                                        >
                                            {formatHour(event.start_datetime)} {event.title?.trim() || 'Evento'}
                                        </button>
                                    ))}
                                    {dayEvents.length > 3 && (
                                        <button
                                            type="button"
                                            className={`px-1 text-[11px] ${gymDarkMutedText}`}
                                            onClick={() => onPickDay(date)}
                                        >
                                            {moreLabel(dayEvents.length - 3)}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    const columns = view === 'day' ? [selectedDate] : weekDays;
    const gridTemplate = view === 'day' ? '64px 1fr' : `64px repeat(${columns.length}, 1fr)`;

    return (
        <div className={`${gymCalendarCard} overflow-hidden`}>
            <div className="overflow-x-auto">
                <div className="min-w-[720px]" style={{ display: 'grid', gridTemplateColumns: gridTemplate }}>
                    <div className={`p-2 text-center text-xs ${gymCalendarGridHeader}`} />
                    {columns.map((date) => (
                        <div key={date.toDateString()} className={`border-l border-b border-gray-200 p-2 text-center ${gymCalendarGridHeader}`}>
                            <div className="text-xs text-gray-500 dark:text-white/60">{dayName(date)}</div>
                            <div className={`mx-auto mt-0.5 flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold ${isSameDay(date, today) ? 'bg-indigo-500 text-white' : 'text-gray-900 dark:text-white'}`}>
                                {date.getDate()}
                            </div>
                        </div>
                    ))}
                    <div>
                        {HOURS.map((hour) => (
                            <div
                                key={hour}
                                className={`border-b border-gray-100 pr-2 text-right text-[11px] text-gray-500 ${gymDarkBorder} ${gymCalendarGridHeader}`}
                                style={{ height: HOUR_HEIGHT }}
                            >
                                {String(hour).padStart(2, '0')}:00
                            </div>
                        ))}
                    </div>
                    {columns.map((date) => (
                        <DayColumn
                            key={date.toDateString()}
                            date={date}
                            events={events}
                            canCreate={canCreate}
                            onEmptySlot={onEmptySlot}
                            onOpen={onOpen}
                            onHover={onHover}
                            onLeave={onLeave}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}
